import React, { useRef, useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./timeblocks.css";
// import "bootstrap-icons/font/bootstrap-icons.css";
import { Toast } from "primereact/toast";
import getTimeBlockBasedonBranchId from "../../../utility/features/api_clients/timeblocks/getTimeBlockBasedonBranch";
import updatemaxtimeblock from "../../../utility/features/api_clients/timeblocks/updatemaxtimeblock";
import savetimeblockbasedonbranch from "../../../utility/features/api_clients/timeblocks/savetimeblockbasedonbranch";
import EditTimeModal from "./editTimeModal";

const defaultDays = {
  Monday: [],
  Tuesday: [],
  Wednesday: [],
  Thursday: [],
  Friday: [],
  Saturday: [],
  Sunday: [],
};

const TimeBlocks = ({branchID}) => {
  const toastRef = useRef(null);
    const isMountedRef = useRef(false);
  const [activeSlots, setActiveSlots] = useState({});
  const [days, setDays] = useState(defaultDays);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const showSuccess = (detail) => {
    toastRef.current?.show({ severity: "success", summary: "Success", detail, life: 3000 });
  };

  const showError = (detail) => {
    toastRef.current?.show({ severity: "error", summary: "Error", detail, life: 5000 });
  };

  const transformApiToDays = (apiData) => {
    const grouped = {};
    apiData.forEach((item) => {
      const day = item.dayName || "Others";
      if (!grouped[day]) grouped[day] = [];
      grouped[day].push({
        time: item.timeBlock,
        count: item.maxPerBlock,
        isStatus: !!item.isActive,
        masterTimeBlockID: item.masterTimeBlockID || item.id, // Store ID for updates
      });
    });
    return grouped;
  };

  const fetchTimeBlocks = async () => {
    setLoading(true);
    setError(null);
    try {
      const branchId = branchID; // use prop or global if available
      const res = await getTimeBlockBasedonBranchId(branchId);
      if (!isMountedRef.current) return;
      if (res && res.data) {
        const grouped = transformApiToDays(res.data);
        setDays(grouped);
      } else {
        const msg = "No data returned";
        setError(msg);
        showError(msg);
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      const msg = err?.message || "Failed to fetch time blocks";
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
       if (isMountedRef.current) setLoading(false);
    }
  };

 useEffect(() => {
    isMountedRef.current = true;
    fetchTimeBlocks();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const toggleSlot = (day, index) => {
    setHasUserInteracted(true);
    setDays((prev) => {
      const updated = { ...prev };
      if (updated[day] && updated[day][index]) {
        const slots = [...updated[day]];
        slots[index] = {
          ...slots[index],
          isStatus: !slots[index].isStatus
        };
        updated[day] = slots;
      }
      return updated;
    });
  };

  const [editing, setEditing] = useState(false);
  const [editInitial, setEditInitial] = useState(null);
  const [editPosition, setEditPosition] = useState(null);

  const openEdit = (day, index, slot) => {
    setEditInitial({ day, time: slot.time, count: slot.count });
    setEditPosition({ day, index });
    setEditing(true);
  };

  const handleSaveEdit = async ({ day: newDay, time: newTime, count: newCount }) => {
    try {
      const branchId = branchID;
      
      // Get masterTimeBlockID from the slot
      const { day: oldDay, index } = editPosition || {};
      const slot = days[oldDay]?.[index];
      const masterTimeBlockID = slot?.masterTimeBlockID || 2; // Default to 2 if not available

      // Call API to update
      const payload = {
        branchID: branchId,
        masterTimeBlockID: masterTimeBlockID,
        maxPerBlock: newCount
      };

      const response = await updatemaxtimeblock(payload);
      
      if (response.error) {
        const msg = "Failed to update time block";
        setError(msg);
        showError(msg);
        return;
      }

      // Reload time blocks after successful update
      await fetchTimeBlocks();

      showSuccess("Time block updated successfully!");

      setEditing(false);
      setEditInitial(null);
      setEditPosition(null);
    } catch (err) {
      const msg = err?.message || "Failed to update time block";
      setError(msg);
      showError(msg);
    }
  };

  const handleCloseEdit = () => {
    setEditing(false);
    setEditInitial(null);
    setEditPosition(null);
  };

  // Check if at least one timeblock is selected
  const hasSelectedTimeblock = () => {
    return Object.values(days).some((slots) =>
      (slots || []).some((slot) => slot.isStatus === true)
    );
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const branchId = branchID;
      
      // Collect all time blocks from all days
      const timeBlockArray = [];
      Object.values(days).forEach((slots) => {
        (slots || []).forEach((slot) => {
          timeBlockArray.push({
            MasterTimeBlockID: slot.masterTimeBlockID,
            isActive: slot.isStatus
          });
        });
      });

      const payload = {
        BranchID: branchId,
        TimeBlock: timeBlockArray
      };

      const response = await savetimeblockbasedonbranch(payload);
      
      if (response?.data?.messageCode == 200) {
        showSuccess("Time blocks saved successfully!");
        await fetchTimeBlocks();
        setHasUserInteracted(false);
      } else {
        const msg = response?.data?.message || "Failed to save time blocks";
        setError(msg);
        showError(msg);
       
      }
    } catch (err) {
      const msg = err?.message || "Failed to save time blocks";
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4 timeblocks-container card-center">
      <Toast ref={toastRef} position="top-right" />
      <h3 className="mb-4 text-center">Weekly Schedule</h3>
      <div className="row">
        {loading ? (
          <div className="text-center">Loading time blocks...</div>
        ) : error ? (
          <div className="text-center text-danger">{error}</div>
        ) : (
          Object.entries(days).map(([day, slots]) => (
            <div className="col-12 mb-3" key={day}>
              <div className="d-flex align-items-start timeblocks-row">
                <div className="me-3 day-label">
                  <div className="rounded p-2 d-flex align-items-center justify-content-center">
                    <h5 className="mb-0">{day}</h5>
                  </div>
                </div>
                <div className="flex-grow-1 d-flex flex-wrap slots-container">
                  {(slots || []).map((slot, index) => {
                    const key = `${day}-${index}`;
                    const isActive = slot.isStatus;
                    return (
                      <div
                        key={key}
                        className={`slot rounded p-2 mb-2 me-2 d-flex align-items-center justify-content-center ${
                          isActive ? "bg-success text-white" : "bg-white"
                        }`}
                        onClick={() => toggleSlot(day, index)}
                        style={{ cursor: "pointer", border: "1px solid #00b140" }}
                      >
                        <span>
                          {slot.time} <span className="">({slot.count})</span>
                        </span>
                        <i
                          className="bi bi-pencil ms-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isActive) {
                              openEdit(day, index, slot);
                            }
                          }}
                          style={{
                            cursor: isActive ? "pointer" : "not-allowed",
                            opacity: isActive ? 1 : 0.3
                          }}
                        ></i>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="text-center mt-4">
        <button 
          className="btn btn-success px-5" 
          onClick={handleSubmit}
          disabled={loading || !hasUserInteracted || !hasSelectedTimeblock()}
        >
          {loading ? 'Submitting...' : 'Submit'}
        </button>
        {hasUserInteracted && hasSelectedTimeblock() && (
          <p className="text-muted mt-2 mb-0">
            Please click on submit to complete the action
          </p>
        )}
      </div>

      <EditTimeModal
        show={editing}
        onClose={handleCloseEdit}
        dayOptions={Object.keys(days)}
        slotOptions={(() => {
          // gather unique slot times across days for selection
          const arr = [];
          Object.values(days).forEach((slist) => {
            (slist || []).forEach((s) => {
              if (!arr.find((x) => x.time === s.time)) arr.push(s);
            });
          });
          return arr;
        })()}
        initial={editInitial}
        onSave={handleSaveEdit}
      />
    </div>
  );
};

export default TimeBlocks;
